import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { UsersSubscriptionPlansInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getUsersSubscriptionPlansColumns } from './UsersSubscriptonPlansColumns';
import { UsersSubscriptionPlansUpdateDialog } from '././UsersSubscriptonPlansUpdateDialog';

const UsersSubscriptonPlansAdminList: React.FC = () => {
    const [plans, setPlans] = useState<UsersSubscriptionPlansInterface[]>([]);
    const [plan, setPlan] = useState<UsersSubscriptionPlansInterface | null>(null);
    const [planId, setPlanId] = useState('');
    const [userId, setUserId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<UsersSubscriptionPlansInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [planToUpdate, setPlanToUpdate] = useState<UsersSubscriptionPlansInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all user subscription plans
    const handleGetAllPlans = async () => {
        setError(null);
        setPlan(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<UsersSubscriptionPlansInterface[]>>('/usersSubscriptionPlans');
            if (result.status && result.data) {
                setPlans(result.data);
            } else {
                setError(result.message || 'Failed to fetch user subscription plans');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch user subscription plans');
        }
    };

    // Fetch plan by id
    const handleGetPlanById = async () => {
        setError(null);
        setPlans([]);
        setSuccess(null);
        if (!planId) return setError('Please enter a User Subscription Plan ID');
        try {
            const result = await apiGet<GenericResponseInterface<UsersSubscriptionPlansInterface>>(`/usersSubscriptionPlans/${planId}`);
            if (result.status && result.data) {
                setPlan(result.data);
            } else {
                setError(result.message || 'Failed to fetch user subscription plan');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch user subscription plan');
        }
    };

    // Fetch plans by userId
    const handleGetPlansByUserId = async () => {
        setError(null);
        setPlan(null);
        setPlans([]);
        setSuccess(null);
        if (!userId) return setError('Please enter a User ID');
        try {
            const result = await apiGet<GenericResponseInterface<UsersSubscriptionPlansInterface[]>>(`/usersSubscriptionPlans/userId/${userId}`);
            if (result.status && result.data) {
                setPlans(result.data);
            } else {
                setError(result.message || 'Failed to fetch user subscription plans by User ID');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch user subscription plans by User ID');
        }
    };

    // Delete plan
    const handleDeletePlan = async () => {
        if (!planToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/usersSubscriptionPlans/delete/${planToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'User subscription plan deleted successfully');
                setDeleteDialogOpen(false);
                setPlanToDelete(null);
                handleGetAllPlans();
            } else {
                setError(result.message || 'Failed to delete user subscription plan');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete user subscription plan');
        }
    };

    // Update plan
    const handleUpdatePlan = async () => {
        if (!planToUpdate || !planToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<UsersSubscriptionPlansInterface>>(
                `/usersSubscriptionPlans/update/${planToUpdate.id}`,
                'PUT',
                planToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'User subscription plan updated successfully');
                setUpdateDialogOpen(false);
                setPlanToUpdate(null);
                handleGetAllPlans();
            } else {
                setUpdateError(result.message || 'Failed to update user subscription plan');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update user subscription plan');
        }
    };

    const columns = getUsersSubscriptionPlansColumns(
        setPlanToDelete,
        setDeleteDialogOpen,
        setPlanToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllPlans();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleGetAllPlans}>Get All User Subscription Plans</Button>
                <Button variant="contained" onClick={handleGetPlanById}>Get Plan by ID</Button>
                <TextField
                    label="User Subscription Plan ID"
                    value={planId}
                    onChange={e => setPlanId(e.target.value)}
                    size="small"
                />
                <Button variant="contained" onClick={handleGetPlansByUserId}>Get by User ID</Button>
                <TextField
                    label="User ID"
                    value={userId}
                    onChange={e => setUserId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {plans.length > 0 && (
                    <DataGrid
                        rows={plans.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {plan && (
                    <DataGrid
                        rows={[{ ...plan, id: plan.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this user subscription plan?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeletePlan}>Delete</Button>
                </DialogActions>
            </Dialog>
            <UsersSubscriptionPlansUpdateDialog
                open={updateDialogOpen}
                planToUpdate={planToUpdate}
                setPlanToUpdate={setPlanToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdatePlan}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default UsersSubscriptonPlansAdminList;